import { compact, get, has, isEmpty } from 'lodash';
import { BinaryTree, Hint, OrderBy, UnAnalyzedAst } from 'common/types/soql';
import { renderBinaryTree } from '../binaryTree';
import { renderDistinct } from '../distinct';
import { renderExpr } from '../expr';
import { renderJoin } from '../join';
import { renderStarSelections, renderUnAnalyzedSelectedExpression as renderSelection } from '../select';
import { renderOrderBy } from '../orderBy';
import { renderTableName } from '../tableName';
import { asString } from '../util';
import { UnAnalyzedAstWithPrerenderedParts, isUnAnalyzedAstWithPrerenderedParts } from './build';

const renderHints = (hints: Hint[]): string | null => {
  if (hints.length > 0) {
    return `HINT(${hints.join(', ')})`;
  } else {
    return null;
  }
};

const renderSelectCasesParts = (
  orderByParts: OrderBy[],
  casesParts: { [key: string]: string } | null | undefined
) => {
  const selectCases: string[] = [];
  const orderBys: string[] = [];

  orderByParts.map((orderConfig, index) => {
    const fieldName = get(orderConfig.expr, 'value', undefined);
    const customSortConfig = get(casesParts, fieldName, undefined);

    // If customSortConfig and fieldName are both empty, return the default orderBy query (e.g., ASC or DESC).
    if (isEmpty(fieldName) || isEmpty(customSortConfig)) {
      orderBys.push(renderOrderBy(orderConfig));
      return renderOrderBy(orderConfig);
    }
    const alias = `__case_alias_${index}__`;
    orderBys.push(alias);
    selectCases.push(`, CASE (${customSortConfig}) as ${alias}`);
    return `CASE (${customSortConfig})`;
  });

  return { selectCases, orderBys };
};

type Ast = UnAnalyzedAst | UnAnalyzedAstWithPrerenderedParts;
const renderSimpleQuery = (ast: Ast): string => {
  const query: Array<string | null> = [];
  const hasLegacyWhereClause = isUnAnalyzedAstWithPrerenderedParts(ast) && ast.legacyWhereClause;
  const { selectCases, orderBys } = renderSelectCasesParts(ast.order_bys, ast.cases);

  query.push(
    compact([
      'SELECT',
      renderHints(ast.hints),
      renderDistinct(ast.distinct),
      renderStarSelections(ast.selection).concat(ast.selection.exprs.map(renderSelection)).join(', '),
      ...((!isEmpty(selectCases) && selectCases) || [])
    ]).join(' ')
  );

  if (ast.from) {
    query.push(`FROM ${renderTableName(ast.from)}`);
  }

  if (ast.joins.length > 0) {
    query.push(ast.joins.map(renderJoin).join(' '));
  }

  if (ast.where) {
    query.push(`WHERE ${renderExpr(ast.where)}`);

    // to allow interop between the old string-based filter => where helpers
    if (hasLegacyWhereClause) {
      query.push('AND', ast.legacyWhereClause);
    }
  } else if (hasLegacyWhereClause) {
    query.push(`WHERE ${ast.legacyWhereClause}`);
  }

  if (!isEmpty(ast.group_bys)) {
    query.push(`GROUP BY ${ast.group_bys.map(renderExpr).join(', ')}`);
  }

  if (ast.having && !isEmpty(ast.having)) {
    query.push(`HAVING ${renderExpr(ast.having)}`);
  }

  if (ast.search) {
    query.push(`SEARCH ${asString(ast.search)}`);
  }

  if (!isEmpty(orderBys)) {
    query.push(`ORDER BY ${orderBys.join(', ')}`);
  }

  if (ast.limit) {
    query.push(`LIMIT ${ast.limit}`);
  }

  if (ast.offset) {
    query.push(`OFFSET ${ast.offset}`);
  }

  return query.join(' ');
};

const isAst = (arg: Ast | unknown): arg is Ast => has(arg, 'selection');
export const renderQuery = (ast: Ast | BinaryTree<Ast>): string =>
  renderBinaryTree<Ast>(ast, isAst, renderSimpleQuery);
